import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import {
    addNotificationFlagToDelete,
    removeNotificationFlagLocally,
} from '../../../slices/notification-flags/notification-flags.slice';
import { VDRFileView } from '../../../slices/marketplace/data-room/models/vdr-file-view.model';
import { VDRFolderView } from '../../../slices/marketplace/data-room/models/vdr-folder-view.model';
import { VDRFolderNavigationHelper } from './VDRFolderNavigationHelper';
import {
    getDataRoomFileFlagCode,
    getFolderFileFlagCodes,
} from './dataRoomFileFlags';

export const useDataRoomFileFlags = (
    engagementId: number,
    navigationHelper: VDRFolderNavigationHelper,
    currentFolderPath: string
) => {
    const dispatch = useDispatch() as any;
    const notificationFlags = useStateSelector(
        (state) => state.notificationFlags.flags
    );
    const notificationFlagsRef = useRef(notificationFlags);
    notificationFlagsRef.current = notificationFlags;
    const navigationHelperRef = useRef(navigationHelper);
    navigationHelperRef.current = navigationHelper;

    const clearFlagsByCodes = useCallback(
        (codes: Set<string>) => {
            notificationFlagsRef.current
                ?.filter((flag) => codes.has(flag.eventCode.toLowerCase()))
                .forEach((flag) => {
                    dispatch(removeNotificationFlagLocally(flag.eventCode));
                    dispatch(
                        addNotificationFlagToDelete({
                            ...flag,
                            refreshAfterRemoval: false,
                        })
                    );
                });
        },
        [dispatch]
    );

    const clearFileFlags = useCallback(
        (files: VDRFileView[]) => {
            clearFlagsByCodes(
                new Set(
                    files.map((file) =>
                        getDataRoomFileFlagCode(
                            engagementId,
                            file
                        ).toLowerCase()
                    )
                )
            );
        },
        [clearFlagsByCodes, engagementId]
    );

    useEffect(() => {
        if (!currentFolderPath) {
            return;
        }
        return () => {
            const folder: VDRFolderView | undefined =
                navigationHelperRef.current.findFolderByPath(currentFolderPath);
            if (folder) {
                clearFlagsByCodes(
                    getFolderFileFlagCodes(engagementId, folder, false)
                );
            }
        };
    }, [currentFolderPath, engagementId, clearFlagsByCodes]);

    return { clearFileFlags };
};
